import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { ApiError } from '../api/client'
import { exportReport, reportsCatalogQuery, reportsValuesQuery, runReport, type ExportFormat } from '../api/reports'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { LoadFailed } from '../ui/LoadFailed'
import { Spinner } from '../ui/Spinner'
import { Builder } from './Builder'
import { ALL_VIEWS, chips, complete, PERIOD_LABELS, sanitize, VIEW_LABELS, type Names } from './describe'
import { PhraseParser } from './parser'
import { ReportBlocks, type Block } from './ReportBlocks'
import { MyReports } from './MyReports'
import { downloadBlocks, printReport } from './reportDownload'
import {
  deleteReport,
  deleteSchedule,
  duplicateReport,
  importLegacy,
  legacyToImport,
  markImported,
  renameReport,
  reportRunsQuery,
  runNow,
  saveReport,
  saveSchedule as putSchedule,
  savedReportsQuery,
  type ReportsCan,
  type SavedBlock,
  type SavedOverview,
  type SavedReport,
  type ScheduleDraft,
} from './reportsStore'
import { ResultView } from './ResultView'
import { DrillPanel } from './DrillPanel'
import { useDrill } from './useDrill'
import { SaveDialog, type SaveFields } from './SaveDialog'
import { formatRun } from './schedule'
import { ScheduleDialog } from './ScheduleDialog'
import { ScheduleSection } from './ScheduleSection'
import { TemplateGallery } from './TemplateGallery'
import { TEMPLATES, templateGallery } from './templates'
import type { CatalogSet, Definition, Locale, ValueIndex, View, Words } from './types'
import './reports.css'
import './saved.css'

/**
 * /app/reports - ZONA DE RAPOARTE DINAMICE (F1a).
 *
 * Trei porti, aratate de la inceput ca trei cartonase: (A) bara "Ce vrei sa
 * vezi?" - fraza se intelege in browser, determinist (`parser.ts`); (B)
 * constructorul pas cu pas, fara nicio fraza; (C) sabloanele, ca lista
 * vizibila cu nume si o propozitie. Butonul "Raport nou" din cap deschide
 * oricand constructorul gol.
 *
 * Bara si constructorul schimba ACEEASI definitie: o fraza inteleasa umple
 * pasii, iar o schimbare in pasi rescrie bucatile de interpretare. Alaturi,
 * previzualizarea se reface la fiecare schimbare. Descarcarea (XLSX, CSV, PDF)
 * sta intr-un singur meniu.
 *
 * Dedesubt, RAPORTUL: blocuri adaugate cu "Adauga la raport" sau dintr-un
 * sablon, cu bara lui: Salveaza, Salveaza o copie, Descarca raportul intreg,
 * Programeaza pe email, Goleste. Fila "Rapoartele mele" tine ce s-a salvat.
 *
 * Salvarea si programarea trec prin `reportsStore.ts`, in baza
 * (`/api/v2/reports/saved`). Ce mai era tinut in browser se aduce o data, la
 * prima deschidere. Emailurile le trimite cronul din ph4; sub raport se vede
 * istoricul rularilor, cu fisierele lor si "Ruleaza acum".
 *
 * Un raport salvat are doua stari, fiecare cu adresa ei:
 *   /reports/saved/<id>        DESCHIS - se arata asa cum e salvat, fara
 *                              constructor si fara unelte pe blocuri; de aici
 *                              Editeaza, Descarca, Programeaza, Ruleaza acum;
 *   /reports/saved/<id>/edit   IN LUCRU pe acelasi id - bara, pasii, blocurile
 *                              se schimba, "Salveaza" scrie peste el.
 * Editarea cere raportul sa fie al omului si `reports.build`; altfel adresa de
 * editare cade pe citire. "/reports" ramane "Raport nou". Fara `navigate`
 * (probele vechi, fara router), starea sta numai in pagina.
 *
 * Butoanele urmeaza drepturile (`can` din raspuns: reports.build /
 * reports.schedule); serverul le verifica oricum, a doua oara.
 *
 * Limba implicita e engleza; alegerea se tine minte pe fiecare om, in
 * localStorage (care poate lipsi sau arunca - atunci ramane engleza).
 */

const EXAMPLES: Record<Locale, string[]> = {
  ro: [
    'cate tichete deschise pe sediu',
    'timpul mediu de rezolvare pe luna anul acesta',
    'conformitatea SLA pe prioritate luna trecuta',
    'pe ce calculatoare e instalata aplicatia Chrome',
    'calculatoare pe versiuni de SO',
    'istoric conectari ultimele 7 zile',
  ],
  en: [
    'how many open tickets by office',
    'average resolution time by month this year',
    'sla compliance by priority last month',
    'which computers have Chrome',
    'devices by operating system',
    'login history last 7 days',
  ],
}

const REASONS: Record<string, { ro: string; en: string }> = {
  no_access: { ro: 'Nu ai acces la aceste date.', en: 'You have no access to this data.' },
  no_access_field: { ro: 'Nu ai acces la campul cerut (date personale).', en: 'You have no access to that field (personal data).' },
  module_inactive: { ro: 'Modulul acestor date nu e activ pe companie.', en: 'The module behind this data is not active for the company.' },
  bad_value: { ro: 'O valoare din raport nu e completa.', en: 'A value in the report is incomplete.' },
}

const DOWNLOADS: { format: ExportFormat; label: string }[] = [
  { format: 'xlsx', label: 'Excel (.xlsx)' },
  { format: 'csv', label: 'CSV (.csv)' },
  { format: 'pdf', label: 'PDF (.pdf)' },
]

const LOCALE_KEY = 'od_reports_locale'

/** Refuzurile serverului la salvare / programare (`SavedReportException`). */
const SAVE_REASONS: Record<string, { ro: string; en: string }> = {
  forbidden: { ro: 'Rolul tau nu are acest drept in rapoarte.', en: 'Your role does not have this right in Reports.' },
  not_owner: { ro: 'Raportul e al altcuiva; poti face o copie.', en: 'This report belongs to someone else; you can make a copy.' },
  not_found: { ro: 'Raportul nu mai exista.', en: 'The report no longer exists.' },
  bad_name: { ro: 'Raportul are nevoie de un nume.', en: 'The report needs a name.' },
  bad_blocks: { ro: 'Raportul nu are blocuri valide.', en: 'The report has no valid blocks.' },
  too_large: { ro: 'Raportul e prea mare.', en: 'The report is too large.' },
  bad_recipient: { ro: 'O adresa de email nu e valida.', en: 'An email address is not valid.' },
  bad_recipients: { ro: 'Adauga intre 1 si 50 de destinatari.', en: 'Add between 1 and 50 recipients.' },
  recipient_domain: { ro: 'Rapoartele pot pleca numai spre adresele firmei.', en: 'Reports can only go to company addresses.' },
  bad_formats: { ro: 'Alege cel putin un format.', en: 'Pick at least one format.' },
  bad_schedule: { ro: 'Programarea nu e completa.', en: 'The schedule is incomplete.' },
  no_schedule: { ro: 'Programeaza intai raportul: rularea pleaca la destinatarii programarii.', en: 'Schedule the report first: the run goes to the schedule recipients.' },
  busy: { ro: 'O rulare a acestui raport e deja in coada.', en: 'A run of this report is already queued.' },
  expired: { ro: 'Fisierul a iesit din termenul de pastrare.', en: 'The file is past its retention period.' },
}

const toSaved = (blocks: Block[]): SavedBlock[] => blocks.map((b) => ({ title: b.title, definition: sanitize(b.definition), wide: b.wide, view: b.definition.view ?? 'table' }))

/** "Descarca raportul": PDF = tiparirea raportului intreg; Excel / CSV = cate un fisier pe bloc. */
function ReportDownloadMenu({ locale, busy, disabled, onPick }: { locale: Locale; busy: string; disabled: boolean; onPick: (f: ExportFormat) => void }) {
  const [open, setOpen] = useState(false)
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const items: { format: ExportFormat; label: string; note: string }[] = [
    { format: 'pdf', label: t('PDF - raportul intreg', 'PDF - the whole report'), note: t('prin tiparirea browserului', 'through the browser print') },
    { format: 'xlsx', label: 'Excel (.xlsx)', note: t('cate un fisier pe bloc', 'one file per block') },
    { format: 'csv', label: 'CSV (.csv)', note: t('cate un fisier pe bloc', 'one file per block') },
  ]
  return (
    <div
      className="od-rep-menu od-rep-report-download"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <button type="button" className="btn btn-default btn-sm" aria-haspopup="menu" aria-expanded={open} disabled={disabled || busy !== ''} onClick={() => setOpen((o) => !o)}>
        {busy || t('Descarca raportul', 'Download report')} <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <ul className="od-rep-menu-list od-rep-menu-right" role="menu">
          {items.map((d) => (
            <li key={d.format} role="none">
              <button
                type="button"
                role="menuitem"
                data-format={d.format}
                onClick={() => {
                  setOpen(false)
                  onPick(d.format)
                }}
              >
                {d.label}
                <span className="od-rep-menu-note">{d.note}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** Limba aleasa data trecuta; engleza daca nu e nimic sau daca stocarea arunca. */
function storedLocale(): Locale {
  try {
    return window.localStorage.getItem(LOCALE_KEY) === 'ro' ? 'ro' : 'en'
  } catch {
    return 'en'
  }
}

function storeLocale(locale: Locale) {
  try {
    window.localStorage.setItem(LOCALE_KEY, locale)
  } catch {
    /* fara stocare: alegerea tine cat pagina */
  }
}

function useDebounced<T>(value: T, ms: number): T {
  const [out, setOut] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setOut(value), ms)
    return () => window.clearTimeout(id)
  }, [value, ms])
  return out
}

/** Titlul unui bloc: propozitia de interpretare, in ambele limbi. */
function sentence(def: Definition, sets: CatalogSet[], names: Names): Words {
  const say = (locale: Locale) => {
    const text = chips(def, sets, locale, names)
      .map((c) => (c.lead ? `${c.lead} ${c.text}` : c.text))
      .join(' ')
    return text.charAt(0).toUpperCase() + text.slice(1)
  }
  return { ro: say('ro') || 'Bloc', en: say('en') || 'Block' }
}

/** "Descarca" ca un singur meniu: Excel, CSV, PDF. */
function DownloadMenu({ locale, busy, disabled, onPick }: { locale: Locale; busy: ExportFormat | null; disabled: boolean; onPick: (f: ExportFormat) => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div
      className="od-rep-menu"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <button type="button" className="btn btn-default btn-sm" aria-haspopup="menu" aria-expanded={open} disabled={disabled || busy !== null} onClick={() => setOpen((o) => !o)}>
        {busy ? (locale === 'ro' ? 'Se descarca…' : 'Downloading…') : locale === 'ro' ? 'Descarca' : 'Download'} <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <ul className="od-rep-menu-list" role="menu">
          {DOWNLOADS.map((d) => (
            <li key={d.format} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  onPick(d.format)
                }}
              >
                {d.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** Unde se duce pagina: un raport salvat (citit sau in lucru), sau "Raport nou" (`id: null`). */
export interface ReportsLocation {
  id: string | null
  edit: boolean
  replace?: boolean
}

export function DynamicReportsPage({ reportId, editing: wantEdit = false, navigate }: { reportId?: string; editing?: boolean; navigate?: (to: ReportsLocation) => void } = {}) {
  const [locale, setLocaleState] = useState<Locale>(storedLocale)
  const [companyId, setCompanyId] = useState(0)
  const [text, setText] = useState('')
  const [manual, setManual] = useState<Definition | null>(null)
  const [building, setBuilding] = useState(false)
  const [builderKey, setBuilderKey] = useState(0)
  const [helpOpen, setHelpOpen] = useState(false)
  const [picked, setPicked] = useState(0)
  const [exporting, setExporting] = useState<ExportFormat | null>(null)
  const [exportError, setExportError] = useState('')
  const [blocks, setBlocks] = useState<Block[]>([])
  const [skipped, setSkipped] = useState(0)
  const [added, setAdded] = useState('')
  const [jump, setJump] = useState<'report' | 'templates' | 'builder' | null>(null)
  const nextId = useRef(1)
  const input = useRef<HTMLInputElement>(null)
  const reportRef = useRef<HTMLDivElement>(null)
  const templatesRef = useRef<HTMLDivElement>(null)
  const builderRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'build' | 'mine'>('build')
  const [current, setCurrent] = useState<SavedReport | null>(null)
  const [origin, setOrigin] = useState<Words | null>(null)
  const [saveMode, setSaveMode] = useState<'save' | 'copy' | 'details' | null>(null)
  const [scheduleAfterSave, setScheduleAfterSave] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [scheduleError, setScheduleError] = useState('')
  const [flash, setFlash] = useState('')
  const [reportBusy, setReportBusy] = useState('')
  const [reportError, setReportError] = useState('')
  const [closing, setClosing] = useState(false)
  /* Raportul deschis e in lucru (true) sau doar aratat (false). Fara `current`, nu conteaza. */
  const [editing, setEditing] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [missing, setMissing] = useState('')
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const setLocale = (l: Locale) => {
    setLocaleState(l)
    storeLocale(l)
  }

  const catalog = useQuery(reportsCatalogQuery(companyId))
  const company = catalog.data?.company_id ?? 0
  const sets = useMemo(() => catalog.data?.datasets ?? [], [catalog.data])

  /* Valorile reale: sediile vin cu catalogul; restul, pe setul pomenit in fraza. */
  const phrase = useDebounced(text, 250)
  const probe = useMemo(() => new PhraseParser({ catalog: sets, values: {}, now: new Date() }), [sets])
  const wanted = useMemo(() => {
    const keys = new Set(probe.namedKeys(phrase))
    if (manual) keys.add(manual.dataset)
    return sets.filter((s) => keys.has(s.key)).flatMap((s) => s.fields.filter((f) => f.values && f.key !== 'office').map((f) => [s.key, f.key] as const))
  }, [probe, phrase, manual, sets])
  const valueQueries = useQueries({ queries: wanted.map(([d, f]) => ({ ...reportsValuesQuery(company, d, f), enabled: company > 0 })) })
  const valueStamp = valueQueries.map((q) => q.dataUpdatedAt).join(',')
  const values: ValueIndex = useMemo(() => {
    const out: ValueIndex = {}
    for (const s of sets) out[s.key] = { office: (catalog.data?.offices ?? []).map((o) => ({ id: o.id, label: o.name, n: 0 })) }
    wanted.forEach(([d, f], i) => {
      const data = valueQueries[i]?.data
      if (data) (out[d] ??= {})[f] = data
    })
    return out
    /* `valueStamp` se schimba exact cand soseste o lista noua de valori; `valueQueries` e alt tablou la fiecare desen. */
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [sets, catalog.data, wanted, valueStamp])

  const parser = useMemo(() => new PhraseParser({ catalog: sets, values, now: new Date(), locale }), [sets, values, locale])
  const parsed = useMemo(() => parser.parse(phrase), [parser, phrase])
  /* O bara golita nu mai are definitie, chiar daca fraza intarziata (250 ms) inca o tine. */
  const definition = manual ?? (text.trim() !== '' && parsed.status === 'ok' ? parsed.definition! : null)
  const runnable = definition ? sanitize(definition) : null
  const runKey = JSON.stringify(runnable)

  const result = useQuery({
    queryKey: ['reports', 'run', company, runKey],
    queryFn: () => runReport(company, runnable!),
    enabled: runnable !== null && company > 0,
    retry: false,
    staleTime: 30 * 1000,
  })
  const drill = useDrill(runnable, sets, result.data, runKey, locale)

  /* Rapoartele salvate pe compania aleasa (ale omului + cele partajate in firma). */
  const savedQ = useQuery(savedReportsQuery(company))
  const saved = useMemo(() => savedQ.data?.reports ?? [], [savedQ.data])
  const savedSchedules = useMemo(() => savedQ.data?.schedules ?? [], [savedQ.data])
  const can: ReportsCan = savedQ.data?.can ?? { view: true, build: false, schedule: false, sensitive: false }
  const runsQ = useQuery(reportRunsQuery(current && current.mine ? current.id : null))

  /* O singura data pe firma: ce a ramas in browser se duce in baza. */
  const importing = useRef(new Set<number>())
  useEffect(() => {
    if (!savedQ.data || !can.build || importing.current.has(company)) return
    const legacy = legacyToImport(company)
    if (legacy.reports.length === 0) return
    importing.current.add(company)
    importLegacy(company, legacy)
      .then((out) => {
        markImported(company, out.map)
        if (out.imported > 0) {
          setFlash(locale === 'ro' ? `${out.imported} rapoarte aduse din browser` : `${out.imported} reports moved from this browser`)
          void queryClient.invalidateQueries({ queryKey: ['reports', 'saved', company] })
        }
      })
      .catch(() => importing.current.delete(company))
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [savedQ.data, company])

  useEffect(() => {
    if (!flash) return
    const id = window.setTimeout(() => setFlash(''), 2500)
    return () => window.clearTimeout(id)
  }, [flash])

  /*
   * Adresa -> stare. Ruleaza numai cand se schimba ADRESA (sau soseste lista),
   * nu cand se schimba raportul din pagina: dupa o salvare, `current` e deja
   * cel nou cand adresa il ajunge din urma, iar "Inapoi" spre /reports
   * inchide raportul.
   */
  const savedReady = savedQ.data !== undefined
  /* Pasul insusi se scrie mai jos, dupa intoarcerile timpurii (are nevoie de `load` / `unload`). */
  const syncRoute = useRef<(() => void) | null>(null)
  useEffect(() => {
    if (navigate && savedReady) syncRoute.current?.()
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, wantEdit, savedReady, company])

  const suggestions = useMemo(() => (manual ? [] : complete(text, sets, values, locale)), [text, sets, values, locale, manual])

  /* Dupa un sablon sau "Raport nou", ecranul se duce la ce s-a deschis. */
  useEffect(() => {
    if (!jump) return
    const target = jump === 'report' ? reportRef.current : jump === 'templates' ? templatesRef.current : builderRef.current
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    setJump(null)
  }, [jump])

  if (catalog.isError) return isForbidden(catalog.error) ? <Forbidden /> : <LoadFailed error={catalog.error} onRetry={() => void catalog.refetch()} what="Reports" />
  if (!catalog.data) return <Spinner />

  const active = building || definition !== null || text.trim() !== ''
  const names: Names = { values, offices: catalog.data.offices }
  const visible = new Set(sets.map((s) => s.key))
  /* Modulele companiei alese, din catalog; un server mai vechi nu le trimite, si atunci se iau din seturile vazute. */
  const modules = catalog.data.modules ?? [...new Set(sets.map((s) => s.module))]
  const gallery = templateGallery(modules, visible)
  const templates = gallery.flatMap((s) => s.cards).filter((c) => c.disabled === null)
  const groupNames = gallery.map((s) => s.group.label[locale]).join(', ')

  const accept = (s: string) => {
    setText(`${s} `)
    setManual(null)
    setPicked(0)
    input.current?.focus()
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) return
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault()
      accept(suggestions[picked] ?? suggestions[0]!)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setPicked((p) => Math.min(p + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setPicked((p) => Math.max(p - 1, 0))
    }
  }
  /** O schimbare din constructor sau din bucati: definitia devine a omului, fraza se goleste. */
  const edit = (def: Definition) => {
    setManual(def)
    setText('')
    setBuilding(true)
  }
  const startOver = () => {
    setText('')
    setManual(null)
    setPicked(0)
    setExportError('')
    setBuilding(false)
  }
  const go = (id: string | null, edit: boolean, replace = false) => navigate?.({ id, edit, replace })
  const newReport = () => {
    setTab('build')
    startOver()
    setBuilding(true)
    setBuilderKey((k) => k + 1)
    setJump('builder')
  }
  const download = async (format: ExportFormat) => {
    if (!runnable) return
    setExporting(format)
    setExportError('')
    try {
      await exportReport(company, runnable, format, locale)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err))
    } finally {
      setExporting(null)
    }
  }
  const addBlock = () => {
    if (!runnable) return
    setBlocks((list) => [...list, { id: nextId.current++, title: sentence(runnable, sets, names), definition: runnable, wide: runnable.view === 'table' }])
    setAdded(runKey)
  }
  const applyTemplate = (key: string) => {
    const template = TEMPLATES.find((x) => x.key === key)
    if (!template) return
    const usable = template.blocks.filter((b) => visible.has(b.definition.dataset))
    setSkipped(template.blocks.length - usable.length)
    setBlocks(usable.map((b) => ({ id: nextId.current++, title: b.title, definition: b.definition, wide: b.definition.view === 'table' })))
    /* Un sablon e un raport nou: numele implicit la salvare e al sablonului. */
    setCurrent(null)
    setEditing(false)
    setOrigin(template.label)
    setJump('report')
  }

  /* ---------------- Raportul salvat ---------------- */

  const dirty = current ? JSON.stringify(toSaved(blocks)) !== JSON.stringify(current.blocks) : blocks.length > 0
  const schedule = current ? (savedSchedules.find((s) => s.report_id === current.id) ?? null) : null
  const companyName = catalog.data.companies.find((c) => c.id === company)?.name ?? ''
  const savedKey = ['reports', 'saved', company]
  const touched = () => {
    void queryClient.invalidateQueries({ queryKey: savedKey })
    void queryClient.invalidateQueries({ queryKey: ['reports', 'runs'] })
  }
  const reasonText = (err: unknown) => {
    const code = err instanceof ApiError && err.details?.error ? String(err.details.error) : ''
    return SAVE_REASONS[code]?.[locale] ?? (err instanceof Error ? err.message : String(err))
  }
  const defaultName = () => origin?.[locale] ?? (blocks.length === 1 ? blocks[0]!.title[locale] : t('Raport nou', 'New report'))
  const saveInitial: SaveFields =
    saveMode === 'copy' && current
      ? { name: `${current.name} (${t('copie', 'copy')})`, description: current.description, visibility: current.visibility }
      : current
        ? { name: current.name, description: current.description, visibility: current.visibility }
        : { name: defaultName(), description: '', visibility: 'private' }

  const persist = async (fields: SaveFields, mode: 'save' | 'copy' | 'details') => {
    setSaveError('')
    try {
      /* Un raport partajat de altcineva nu se scrie peste: "Salveaza" face o copie a omului. */
      const own = current?.mine ? current.id : undefined
      const out = await saveReport(company, {
        ...fields,
        id: mode === 'copy' ? undefined : own,
        blocks: mode === 'details' && current ? current.blocks : toSaved(blocks),
      })
      queryClient.setQueryData<SavedOverview>(savedKey, (old) => (old ? { ...old, reports: [out, ...old.reports.filter((r) => r.id !== out.id)] } : old))
      setCurrent(out)
      setOrigin(null)
      setSaveMode(null)
      /* Dupa salvare raportul ramane in lucru, pe id-ul lui; "Detalii" nu schimba starea. */
      if (mode !== 'details') {
        setEditing(true)
        if (current?.id !== out.id || !editing) go(out.id, true, current?.id === out.id)
      }
      touched()
      setFlash(mode === 'copy' ? t('Copie salvata', 'Copy saved') : t('Salvat', 'Saved'))
      if (scheduleAfterSave) {
        setScheduleAfterSave(false)
        if (can.schedule) setScheduleOpen(true)
      }
    } catch (err) {
      setSaveError(reasonText(err))
    }
  }
  const save = () => {
    if (current?.mine) void persist({ name: current.name, description: current.description, visibility: current.visibility }, 'save')
    else if (current) setSaveMode('copy')
    else setSaveMode('save')
  }
  const openSchedule = () => {
    if (current?.mine) setScheduleOpen(true)
    else {
      setScheduleAfterSave(true)
      setSaveMode('save')
    }
  }
  const saveSchedule = async (draft: ScheduleDraft) => {
    setScheduleError('')
    try {
      await putSchedule(draft)
      setScheduleOpen(false)
      touched()
      setFlash(t('Programare salvata', 'Schedule saved'))
    } catch (err) {
      setScheduleError(reasonText(err))
    }
  }
  const queueRun = async () => {
    if (!current) return
    setReportError('')
    try {
      await runNow(current.id)
      setFlash(t('Rularea a intrat in coada; pleaca in cateva minute', 'The run is queued; it goes out within minutes'))
      void queryClient.invalidateQueries({ queryKey: ['reports', 'runs', current.id] })
    } catch (err) {
      setReportError(reasonText(err))
    }
  }
  const mayEdit = (r: SavedReport) => can.build && r.mine
  /** Blocurile raportului, asa cum sunt salvate (o editare nesalvata se pierde). */
  const revert = (r: SavedReport) => {
    setBlocks(r.blocks.map((b) => ({ id: nextId.current++, title: b.title, definition: b.definition, wide: b.wide })))
    setSkipped(0)
    setAdded('')
    setClosing(false)
    setLeaving(false)
    startOver()
  }
  /** Pune raportul in pagina: aratat (edit=false) sau in lucru, pe acelasi id. */
  const load = (r: SavedReport, edit: boolean) => {
    revert(r)
    setCurrent(r)
    setOrigin(null)
    setEditing(edit)
    setTab('build')
    setJump('report')
  }
  const unload = () => {
    setCurrent(null)
    setEditing(false)
    setOrigin(null)
    setBlocks([])
    setSkipped(0)
    setAdded('')
    setClosing(false)
    setLeaving(false)
  }
  const openReport = (r: SavedReport, edit = false) => {
    const allowed = edit && mayEdit(r)
    load(r, allowed)
    setMissing('')
    go(r.id, allowed)
  }
  const closeReport = () => {
    unload()
    setMissing('')
    go(null, false)
  }
  /** Din citire in lucru, pe acelasi raport. */
  const startEdit = () => {
    if (!current || !mayEdit(current)) return
    setEditing(true)
    go(current.id, true)
  }
  /** Din lucru inapoi la citire: ce nu s-a salvat se lasa. */
  const stopEdit = () => {
    if (!current) return
    revert(current)
    setEditing(false)
    go(current.id, false)
  }
  syncRoute.current = () => {
    if (!reportId) {
      setMissing('')
      if (current) unload()
      return
    }
    const r = current?.id === reportId ? current : saved.find((x) => x.id === reportId)
    if (!r) {
      setMissing(reportId)
      return
    }
    setMissing('')
    const allowed = wantEdit && mayEdit(r)
    if (current?.id !== reportId) load(r, allowed)
    else if (editing !== allowed) {
      if (!allowed) revert(r)
      setEditing(allowed)
    }
    /* Adresa de editare pe un raport care nu se poate schimba cade pe citire. */
    if (wantEdit && !allowed) go(reportId, false, true)
  }
  const downloadWhole = async (format: ExportFormat) => {
    setReportError('')
    if (format === 'pdf') {
      printReport(current?.name ?? defaultName())
      return
    }
    try {
      await downloadBlocks(company, blocks, format, locale, (done, total) => setReportBusy(done < total ? t(`Se descarca ${done + 1}/${total}…`, `Downloading ${done + 1}/${total}…`) : ''))
    } catch (err) {
      setReportError(err instanceof Error ? err.message : String(err))
    } finally {
      setReportBusy('')
    }
  }
  const error = result.error instanceof ApiError ? result.error : null
  const reason = error?.details?.error ? String(error.details.error) : ''
  const showReport = active || blocks.length > 0 || skipped > 0 || current !== null
  /* Raportul salvat, doar aratat: fara bara, fara pasi, fara sabloane, fara unelte pe blocuri. */
  const viewing = current !== null && !editing
  /* Adresa cere un raport care inca n-a sosit: nu se arata "Raport nou" intre timp. */
  const waiting = navigate !== undefined && reportId !== undefined && current?.id !== reportId && missing === '' && !savedQ.isError
  const reportName = current?.name ?? defaultName()
  const count = (n: number) => t(`${n} ${n === 1 ? 'bloc' : 'blocuri'}`, `${n} ${n === 1 ? 'block' : 'blocks'}`)

  return (
    <div className="od-rep">
      <div className="od-rep-head">
        <div className="od-rep-title">
          <h2>{t('Rapoarte', 'Reports')}</h2>
          <p className="od-rep-sub">
            {t(
              'Transforma datele tale intr-un tabel, o cifra sau un grafic: intreaba in cuvinte simple, construieste pas cu pas sau porneste de la un sablon.',
              'Turn your data into a table, a number or a chart: ask in plain words, build it step by step, or start from a template.'
            )}{' '}
            <button type="button" className="od-rep-link" aria-expanded={helpOpen} onClick={() => setHelpOpen((o) => !o)}>
              {t('Cum functioneaza?', 'How does this work?')}
            </button>
          </p>
        </div>
        <div className="od-rep-head-tools">
          {catalog.data.companies.length > 1 ? (
            <select
              className="form-control input-sm"
              value={company}
              aria-label={t('Compania', 'Company')}
              onChange={(e) => {
                setCompanyId(Number(e.target.value))
                setManual(null)
                if (current) closeReport()
              }}
            >
              {catalog.data.companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : null}
          <div className="btn-group btn-group-sm" role="group" aria-label={t('Limba', 'Language')}>
            {(['en', 'ro'] as Locale[]).map((l) => (
              <button key={l} type="button" className={`btn btn-default${locale === l ? ' active' : ''}`} aria-pressed={locale === l} onClick={() => setLocale(l)}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm od-rep-new"
            onClick={() => {
              if (current || missing) closeReport()
              newReport()
            }}
          >
            + {t('Raport nou', 'New report')}
          </button>
        </div>
      </div>

      {helpOpen ? (
        <ol className="od-rep-help">
          <li>
            <b>{t('Alege ce vrei sa vezi.', 'Pick what you want to see.')}</b> {t('Scrie o intrebare, mergi pas cu pas sau ia un sablon.', 'Type a question, go step by step, or take a template.')}
          </li>
          <li>
            <b>{t('Potriveste.', 'Adjust it.')}</b>{' '}
            {t('Intrebarea si pasii schimba acelasi raport; rezultatul se vede pe loc, alaturi.', 'The question and the steps change the same report; the result updates right next to them.')}
          </li>
          <li>
            <b>{t('Strange si descarca.', 'Collect and download.')}</b>{' '}
            {t('"Adauga la raport" pastreaza rezultatul ca bloc; "Descarca" il da ca Excel, CSV sau PDF.', '"Add to report" keeps the result as a block; "Download" gives it as Excel, CSV or PDF.')}
          </li>
        </ol>
      ) : null}

      <div className="od-rep-tabs" role="tablist" aria-label={t('Rapoarte', 'Reports')}>
        <button type="button" role="tab" aria-selected={tab === 'build'} className={`od-rep-tab${tab === 'build' ? ' active' : ''}`} onClick={() => setTab('build')}>
          <span className="od-rep-tab-name">{current ? current.name : t('Raport nou', 'New report')}</span>
          {current ? <span className="od-rep-tab-mode">{editing ? t('in lucru', 'editing') : t('citire', 'view')}</span> : null}
          {dirty ? <span className="od-rep-dot" title={t('Modificari nesalvate', 'Unsaved changes')} /> : null}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'mine'} className={`od-rep-tab od-rep-tab-mine${tab === 'mine' ? ' active' : ''}`} onClick={() => setTab('mine')}>
          {t('Rapoartele mele', 'My reports')} <span className="od-rep-tab-n">{saved.length}</span>
        </button>
      </div>

      {tab === 'mine' ? (
        <MyReports
          reports={saved}
          schedules={savedSchedules}
          locale={locale}
          currentId={current?.id ?? null}
          can={can}
          loading={savedQ.isLoading}
          error={savedQ.isError ? reasonText(savedQ.error) : ''}
          onOpen={(r) => openReport(r, false)}
          onEdit={(r) => openReport(r, true)}
          onRename={(r, name) => {
            void renameReport(r.id, name)
              .then((out) => {
                if (current?.id === r.id) setCurrent(out)
                touched()
              })
              .catch((err) => setFlash(reasonText(err)))
          }}
          onDuplicate={(r) => {
            void duplicateReport(r.id, `${r.name} (${t('copie', 'copy')})`)
              .then(touched)
              .catch((err) => setFlash(reasonText(err)))
          }}
          onDelete={(r) => {
            void deleteReport(r.id)
              .then(() => {
                if (current?.id === r.id) closeReport()
                touched()
              })
              .catch((err) => setFlash(reasonText(err)))
          }}
          onNew={() => {
            closeReport()
            newReport()
          }}
        />
      ) : null}

      {tab === 'build' && missing ? (
        <div className="od-rep-empty od-rep-missing" role="alert">
          {t('Raportul nu exista pe aceasta companie sau nu il poti vedea.', 'This report does not exist on this company or you cannot see it.')}{' '}
          <button type="button" className="od-rep-link" onClick={() => setTab('mine')}>
            {t('Rapoartele mele', 'My reports')}
          </button>
        </div>
      ) : null}

      {tab === 'build' && waiting ? <Spinner /> : null}

      {tab === 'build' && !waiting ? (
        <>
      {sets.length === 0 ? (
        <div className="od-rep-empty">{t('Nu exista date pe care sa le poti cere pe aceasta companie (module inactive sau fara drept).', 'There is no data you can ask for on this company (modules inactive or no rights).')}</div>
      ) : null}

      {!viewing ? (
      <div className={`od-rep-start${active ? ' od-rep-start-active' : ''}`}>
        <div className="od-rep-card od-rep-card-ask">
          {!active ? (
            <div className="od-rep-card-head">
              <span className="od-rep-card-mark" aria-hidden="true">
                ?
              </span>
              <h3>{t('Intreaba in cuvinte simple', 'Ask in plain words')}</h3>
            </div>
          ) : null}
          {!active ? <p className="od-rep-card-text">{t('Scrie ce vrei sa vezi; cuvintele se potrivesc pe datele tale.', 'Type what you want to see; your words are matched to your data.')}</p> : null}
          <div className="od-rep-askbar">
            <input
              ref={input}
              type="text"
              className="form-control od-rep-input"
              placeholder={t('Ce vrei sa vezi?', 'What do you want to see?')}
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                setManual(null)
                setPicked(0)
              }}
              onKeyDown={onKey}
              aria-label={t('Ce vrei sa vezi?', 'What do you want to see?')}
              autoComplete="off"
              spellCheck={false}
            />
            {suggestions.length > 0 ? (
              <ul className="od-rep-suggest" role="listbox">
                {suggestions.map((s, i) => (
                  <li key={s} role="option" aria-selected={i === picked} className={i === picked ? 'active' : undefined} onMouseDown={(e) => (e.preventDefault(), accept(s))}>
                    {s}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {!active ? (
            <div className="od-rep-examples">
              <span className="od-rep-caption">{t('De exemplu:', 'For example:')}</span>
              {EXAMPLES[locale].map((ex) => (
                <button key={ex} type="button" className="od-rep-example" onClick={() => accept(ex)}>
                  {ex}
                </button>
              ))}
            </div>
          ) : null}

          {!manual && parsed.status === 'unknown' && text.trim() !== '' ? (
            <div className="od-rep-note">
              {parsed.unknown.length === 0
                ? t(
                    'Spune si despre ce date e vorba: tichete, calculatoare, aplicatii, actualizari, conectari, imprimante - sau alege-le mai jos, la pasul 1.',
                    'Say which data you mean: tickets, computers, applications, updates, logins, printers - or pick it below, in step 1.'
                  )
                : parsed.unknown.map((u) => (
                    <div key={`${u.start}`}>
                      {u.reason ? (
                        <>
                          <mark>{u.word}</mark>: {u.reason[locale]}
                        </>
                      ) : (
                        <>
                          {t('Nu stiu ce e', 'I do not know what')} <mark>{u.word}</mark>
                          {locale === 'en' ? ' means' : ''}.
                        </>
                      )}
                      {u.suggestions.length > 0 ? (
                        <>
                          {' '}
                          {t('Ai vrut:', 'Did you mean:')}{' '}
                          {u.suggestions.map((s) => (
                            <button key={s} type="button" className="od-rep-example" onClick={() => accept(text.slice(0, u.start) + s + text.slice(u.end))}>
                              {s}
                            </button>
                          ))}
                        </>
                      ) : null}
                    </div>
                  ))}
            </div>
          ) : null}

          {!manual && parsed.status === 'ask' && parsed.question ? (
            <div className="od-rep-note">
              {parsed.question.text[locale]}{' '}
              {parsed.question.options.map((o, i) => (
                <button key={i} type="button" className="btn btn-default btn-sm" onClick={() => setManual(o.definition)}>
                  {o.label[locale]}
                </button>
              ))}
            </div>
          ) : null}

          {!manual && parsed.corrections.length > 0 ? (
            <div className="od-rep-hint">
              {t('Am citit', 'Read')} {parsed.corrections.map((c) => `„${c.from}" ${t('ca', 'as')} „${c.to}"`).join(', ')}.
            </div>
          ) : null}
        </div>

        {!active ? (
          <div className="od-rep-card">
            <div className="od-rep-card-head">
              <span className="od-rep-card-mark" aria-hidden="true">
                ☰
              </span>
              <h3>{t('Construieste pas cu pas', 'Build step by step')}</h3>
            </div>
            <p className="od-rep-card-text">
              {t(
                'Fara nicio fraza: alegi datele, ce numeri, cum grupezi, filtrele si graficul. Rezultatul se vede pe parcurs.',
                'No typing needed: pick the data, what to count, how to group, the filters and the chart. You see the result as you go.'
              )}
            </p>
            <button type="button" className="btn btn-default btn-sm od-rep-card-cta" onClick={newReport}>
              {t('Incepe', 'Start building')} →
            </button>
          </div>
        ) : null}

        {!active && !current ? (
          <div className="od-rep-card">
            <div className="od-rep-card-head">
              <span className="od-rep-card-mark" aria-hidden="true">
                ▦
              </span>
              <h3>{t('Porneste de la un sablon', 'Start from a template')}</h3>
            </div>
            <p className="od-rep-card-text">
              {t(
                `${templates.length} rapoarte gata facute${groupNames ? ` - ${groupNames}` : ''}. Alegi unul si il schimbi cum vrei.`,
                `${templates.length} ready-made reports${groupNames ? ` - ${groupNames}` : ''}. Pick one and change anything in it.`
              )}
            </p>
            <button type="button" className="btn btn-default btn-sm od-rep-card-cta" onClick={() => setJump('templates')}>
              {t('Vezi sabloanele', 'Browse templates')} ↓
            </button>
          </div>
        ) : null}
      </div>
      ) : null}

      {active && !viewing ? (
        <div className={`od-rep-work${definition ? '' : ' od-rep-work-picking'}`}>
          <div className="od-rep-work-build" ref={builderRef}>
            <div className="od-rep-section-head">
              <h3>{t('Pas cu pas', 'Step by step')}</h3>
              <button type="button" className="od-rep-link" onClick={startOver}>
                ← {t('Inapoi la inceput', 'Back to start')}
              </button>
            </div>
            <Builder key={builderKey} catalog={sets} definition={definition} values={definition ? (values[definition.dataset] ?? {}) : {}} locale={locale} onChange={edit} />
          </div>

          {definition ? (
            <div className="od-rep-work-preview">
              <div className="od-rep-section-head">
                <h3>{t('Previzualizare', 'Preview')}</h3>
                {result.data ? (
                  <span className="od-rep-meta">
                    {result.data.truncated ? t(`primele ${result.data.limit} randuri, `, `first ${result.data.limit} rows, `) : ''}
                    {result.data.elapsed_ms} ms
                  </span>
                ) : null}
              </div>

              <div className="od-rep-chips">
                {chips(definition, sets, locale, names).map((c, i) => (
                  <span key={i} className={`od-rep-chip od-rep-chip-${c.kind}`}>
                    {c.lead ? <span className="od-rep-lead">{c.lead} </span> : null}
                    {c.kind === 'period' ? (
                      <select
                        className="od-rep-chip-select"
                        value={definition.period?.key ?? 'all'}
                        onChange={(e) => edit({ ...definition, period: { key: e.target.value as never } })}
                        aria-label={t('Perioada', 'Period')}
                      >
                        {(['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month', 'this_quarter', 'last_quarter', 'this_year', 'last_year', 'last_7_days', 'last_30_days', 'last_90_days'] as const).map((k) => (
                          <option key={k} value={k}>
                            {PERIOD_LABELS[k][locale]}
                          </option>
                        ))}
                        {definition.period && ['custom', 'last_n_days'].includes(definition.period.key) ? <option value={definition.period.key}>{c.text}</option> : null}
                      </select>
                    ) : (
                      <span>{c.text}</span>
                    )}
                    {c.remove ? (
                      <button type="button" className="od-rep-x" aria-label={t('Scoate', 'Remove')} onClick={() => edit(c.remove!())}>
                        ×
                      </button>
                    ) : null}
                  </span>
                ))}
                <span className="od-rep-chip od-rep-chip-view">
                  <select className="od-rep-chip-select" value={definition.view ?? 'table'} onChange={(e) => edit({ ...definition, view: e.target.value as View })} aria-label={t('Vedere', 'View')}>
                    {ALL_VIEWS.map((v) => (
                      <option key={v} value={v}>
                        {VIEW_LABELS[v][locale]}
                      </option>
                    ))}
                  </select>
                </span>
              </div>

              <div className="od-rep-actions">
                <button type="button" className="btn btn-primary btn-sm od-rep-add-block" disabled={!runnable} onClick={addBlock}>
                  {added === runKey ? `✓ ${t('Adaugat in raport', 'Added to report')}` : `+ ${t('Adauga la raport', 'Add to report')}`}
                </button>
                <DownloadMenu locale={locale} busy={exporting} disabled={!runnable} onPick={(f) => void download(f)} />
              </div>
              {exportError ? <div className="alert alert-danger">{exportError}</div> : null}

              {result.isFetching && !result.data ? <Spinner /> : null}
              {error ? <div className="alert alert-warning">{REASONS[reason]?.[locale] ?? error.message}</div> : null}
              {result.data ? (
                <>
                  {result.data.notes.map((n) => (
                    <div key={n} className="od-rep-hint">
                      {n.startsWith('restrict_not_applied:')
                        ? t(`Restrangerea pe ${n.split(':')[1]} nu se aplica acestor date.`, `The ${n.split(':')[1]} restriction does not apply to this data.`)
                        : n}
                    </div>
                  ))}
                  <ResultView result={result.data} locale={locale} onDrill={drill.onDrill} />
                  {drill.drill ? <DrillPanel company={company} drill={drill.drill} locale={locale} onClose={drill.close} /> : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {showReport ? (
        <div className="od-rep-report" ref={reportRef}>
          <div className="od-rep-print-head">
            <h1>{reportName}</h1>
            {current?.description ? <p>{current.description}</p> : null}
            <p className="od-rep-meta">
              {companyName ? `${companyName} · ` : ''}
              {formatRun(new Date(), locale)}
            </p>
          </div>
          <div className="od-rep-report-head od-rep-noprint">
            <h3 className="od-rep-report-title">
              {current && mayEdit(current) ? (
                <button type="button" className="od-rep-report-name" title={t('Nume, descriere, cine il vede', 'Name, description, who can see it')} onClick={() => setSaveMode('details')}>
                  {current.name}
                </button>
              ) : current ? (
                <span className="od-rep-report-name od-rep-report-name-ro">{current.name}</span>
              ) : (
                t('Raportul tau', 'Your report')
              )}{' '}
              <span className="od-rep-count">{count(blocks.length)}</span>
              {dirty ? (
                <span className="od-rep-dirty">
                  <span className="od-rep-dot" aria-hidden="true" />
                  {current ? t('Modificari nesalvate', 'Unsaved changes') : t('Nesalvat', 'Not saved')}
                </span>
              ) : null}
              {flash ? (
                <span className="od-rep-flash" role="status">
                  ✓ {flash}
                </span>
              ) : null}
            </h3>
            <div className="od-rep-toolbar">
              {viewing && current ? (
                <>
                  {mayEdit(current) ? (
                    <button type="button" className="btn btn-primary btn-sm od-rep-edit" onClick={startEdit}>
                      {t('Editeaza', 'Edit')}
                    </button>
                  ) : null}
                  {can.build && !current.mine ? (
                    <button type="button" className="btn btn-default btn-sm" disabled={blocks.length === 0} onClick={() => setSaveMode('copy')}>
                      {t('Salveaza o copie', 'Save as copy')}
                    </button>
                  ) : null}
                  <ReportDownloadMenu locale={locale} busy={reportBusy} disabled={blocks.length === 0} onPick={(f) => void downloadWhole(f)} />
                  {can.schedule && can.build && current.mine ? (
                    <button type="button" className="btn btn-default btn-sm od-rep-schedule-btn" onClick={openSchedule}>
                      <span aria-hidden="true">✉</span> {t('Programeaza', 'Schedule')}
                    </button>
                  ) : null}
                </>
              ) : blocks.length > 0 || current ? (
                <>
                  {can.build ? (
                    <button type="button" className={`btn btn-sm od-rep-save${current && !dirty ? ' btn-default od-rep-save-done' : ' btn-primary'}`} disabled={blocks.length === 0 || (current !== null && !dirty)} onClick={save}>
                      {current && !dirty ? `✓ ${t('Salvat', 'Saved')}` : t('Salveaza', 'Save')}
                    </button>
                  ) : null}
                  {current && can.build ? (
                    <button type="button" className="btn btn-default btn-sm" disabled={blocks.length === 0} onClick={() => setSaveMode('copy')}>
                      {t('Salveaza o copie', 'Save as copy')}
                    </button>
                  ) : null}
                  <ReportDownloadMenu locale={locale} busy={reportBusy} disabled={blocks.length === 0} onPick={(f) => void downloadWhole(f)} />
                  {can.schedule && can.build && (!current || current.mine) ? (
                    <button type="button" className="btn btn-default btn-sm od-rep-schedule-btn" disabled={blocks.length === 0 && !current} onClick={openSchedule}>
                      <span aria-hidden="true">✉</span> {t('Programeaza', 'Schedule')}
                    </button>
                  ) : null}
                </>
              ) : null}
              {blocks.length > 0 && !viewing ? (
                <button type="button" className="btn btn-default btn-sm" onClick={() => (setBlocks([]), setSkipped(0), setAdded(''))}>
                  {t('Goleste', 'Clear')}
                </button>
              ) : null}
              {current && editing ? (
                leaving ? (
                  <span className="od-rep-confirm" role="alert">
                    {t('Renunti la modificari?', 'Discard your changes?')}
                    <button type="button" className="btn btn-danger btn-sm" onClick={stopEdit}>
                      {t('Renunta', 'Discard')}
                    </button>
                    <button type="button" className="btn btn-default btn-sm" onClick={() => setLeaving(false)}>
                      {t('Nu', 'No')}
                    </button>
                  </span>
                ) : (
                  <button type="button" className="btn btn-default btn-sm od-rep-done" onClick={() => (dirty ? setLeaving(true) : stopEdit())}>
                    {t('Gata', 'Done')}
                  </button>
                )
              ) : null}
              {current ? (
                closing ? (
                  <span className="od-rep-confirm" role="alert">
                    {t('Inchizi fara sa salvezi?', 'Close without saving?')}
                    <button type="button" className="btn btn-danger btn-sm" onClick={closeReport}>
                      {t('Inchide', 'Close')}
                    </button>
                    <button type="button" className="btn btn-default btn-sm" onClick={() => setClosing(false)}>
                      {t('Nu', 'No')}
                    </button>
                  </span>
                ) : (
                  <button type="button" className="btn btn-link btn-sm od-rep-close" onClick={() => (dirty ? setClosing(true) : closeReport())}>
                    {t('Inchide raportul', 'Close report')}
                  </button>
                )
              ) : null}
            </div>
          </div>
          {/* Programarea sta sus, sub antet, deasupra blocurilor: e primul lucru pe care il cauti la un raport salvat. */}
          {current && current.mine && can.schedule ? (
            <ScheduleSection
              schedule={schedule}
              runs={runsQ.data ?? []}
              locale={locale}
              onCreate={openSchedule}
              onEdit={() => setScheduleOpen(true)}
              onRunNow={() => void queueRun()}
              onToggle={() => {
                if (!schedule) return
                const { id: _id, next_run_at: _n, last_run_at: _l, ...rest } = schedule
                void _id
                void _n
                void _l
                void putSchedule({ ...rest, enabled: !schedule.enabled })
                  .then(touched)
                  .catch((err) => setReportError(reasonText(err)))
              }}
              onDelete={() => {
                if (!schedule) return
                void deleteSchedule(schedule.report_id)
                  .then(touched)
                  .catch((err) => setReportError(reasonText(err)))
              }}
            />
          ) : null}
          <p className="od-rep-hint od-rep-report-help od-rep-noprint">
            {viewing
              ? current && mayEdit(current)
                ? t('Raportul, asa cum e salvat. "Editeaza" il deschide in lucru: blocuri, filtre, perioade.', 'The report as saved. "Edit" opens it for changes: blocks, filters, periods.')
                : t('Raportul, asa cum e salvat.', 'The report as saved.')
              : blocks.length === 0
              ? t('Raportul e gol. "Adauga la raport" pastreaza aici rezultatul din previzualizare, ca bloc; apoi construiesti urmatorul.', 'Your report is empty. "Add to report" keeps the preview result here as a block; then you build the next one.')
              : t('Fiecare bloc se poate muta, lati, schimba ca vedere sau scoate. Salveaza raportul ca sa-l gasesti in "Rapoartele mele" si sa-l primesti pe email.', 'Each block can be moved, widened, shown another way or removed. Save the report to find it in "My reports" and get it by email.')}
          </p>
          {saveError ? <div className="alert alert-danger od-rep-noprint">{saveError}</div> : null}
          {current && !current.mine ? (
            <p className="od-rep-hint od-rep-noprint">
              {can.build
                ? t('Raport partajat in firma: il poti rula si descarca; "Salveaza o copie" face unul al tau, pe care il poti schimba.', 'Shared in your company: you can run and download it; "Save as copy" makes your own, which you can change.')
                : t('Raport partajat in firma: il poti rula si descarca.', 'Shared in your company: you can run and download it.')}
            </p>
          ) : null}
          {skipped > 0 ? (
            <div className="od-rep-hint">
              {t(
                `${skipped} blocuri din sablon nu se arata: datele lor nu sunt active pe aceasta companie sau nu ai drept pe ele.`,
                `${skipped} template blocks are hidden: their data is not active for this company or you have no right to it.`
              )}
            </div>
          ) : null}
          {reportError ? <div className="alert alert-danger od-rep-noprint">{reportError}</div> : null}
          <ReportBlocks blocks={blocks} sets={sets} sensitive={catalog.data.sensitive} company={company} locale={locale} readOnly={viewing} onChange={setBlocks} />
        </div>
      ) : null}

      {!active && !current ? (
        <div className="od-rep-templates" ref={templatesRef}>
          <TemplateGallery modules={modules} visible={visible} locale={locale} onPick={applyTemplate} />
        </div>
      ) : null}
        </>
      ) : null}

      <SaveDialog
        open={saveMode !== null}
        mode={saveMode ?? 'save'}
        initial={saveInitial}
        locale={locale}
        hint={scheduleAfterSave ? t('Salveaza intai raportul; apoi alegi cand pleaca pe email.', 'Save the report first; then you choose when it goes out by email.') : ''}
        onClose={() => {
          setSaveMode(null)
          setScheduleAfterSave(false)
        }}
        error={saveError}
        onSave={(fields) => void persist(fields, saveMode ?? 'save')}
      />
      {current ? (
        <ScheduleDialog
          open={scheduleOpen}
          report={current}
          schedule={schedule}
          locale={locale}
          error={scheduleError}
          onClose={() => {
            setScheduleOpen(false)
            setScheduleError('')
          }}
          onSave={(draft) => void saveSchedule(draft)}
        />
      ) : null}
    </div>
  )
}
